import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  ArrowBack,
  ArrowDownward,
  ArrowUpward,
  ContentCopy,
  DeleteOutline,
  ExpandMore,
  PlayArrow,
  Publish,
  Save,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';

import {
  IArnoValidation,
  IArnoValidationIssue,
  useArnoTemplateMigration,
  useArnoTemplateContent,
  useArnoTemplateVersions,
  usePublishArnoTemplateVersion,
  useSaveArnoTemplateContent,
  useValidateArnoTemplateVersion,
  useArnoSelectorCatalog,
} from '../../hooks/reactQuerys/ArnoAdminQueries';
import {
  COMMON_NODE_KINDS,
  MORE_NODE_KINDS,
  NODE_KIND_HINT,
  blockTargetsOf,
  conditionSummary,
  duplicateNode,
  IEditorSection,
  insertAt,
  isDirty,
  moveItem,
  newNode,
  newSection,
  NodeKind,
  NODE_KIND_LABEL,
  nodeSummary,
  questionChoices,
  removeAt,
  replaceAt,
  sectionSummary,
} from './editorModel';
import { NodeEditor } from './NodeEditor';
import { DecisionTreeCanvas } from './DecisionTreeCanvas';
import { fieldLabelResolver } from './fieldPathLabel';
import { ConditionBuilder } from './ConditionBuilder';
import { MigrationPanel } from './MigrationPanel';
import { apiErrorText } from './apiErrorText';
import { PreviewTransactionPicker } from './PreviewTransactionPicker';
import {
  ReaderSelection,
  SectionReader,
  sampleResolver,
} from './SectionReader';
import { ReaderDetails } from './ReaderDetails';
import { InlineTextEdit } from './InlineTextEdit';
import { editableInPlace, nodeAt, replaceNodeAt } from './readingModel';
import { TestRunDialog } from './TestRunDialog';
import { lifecycleChip } from './arnoAdminLabels';
import { useAlert } from '../../context/AlertContext';
import { useConfirmation } from '../../context/ConfirmationContext';

/**
 * Editor für einen in DiNo angelegten Entscheidungsbaum (#1245).
 *
 * Links die Abschnitte, rechts der Inhalt des gewählten Abschnitts. Bewusst kein
 * Zeichenbrett mit Pfeilen: die Struktur ist eine Liste, und eine Liste lässt
 * sich zuverlässig bearbeiten.
 *
 * Gearbeitet wird immer an einer Entwurfsversion. Eine veröffentlichte Version
 * ist nur lesbar; wer sie ändern will, legt die nächste an. Veröffentlicht wird
 * erst, wenn die Prüfung keinen Fehler mehr meldet.
 */

export const DecisionTreeEditor: React.FC = () => {
  const { idDecisionTemplateVersion } = useParams();
  const versionId = Number(idDecisionTemplateVersion);
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();

  const { templateContent, isLoadingTemplateContent, refetchTemplateContent } =
    useArnoTemplateContent(versionId);
  const { templateVersions } = useArnoTemplateVersions(
    templateContent?.idDecisionTemplate
  );
  const { saveTemplateContent, isSavingTemplateContent } =
    useSaveArnoTemplateContent();
  const { validateTemplateVersion, isValidatingTemplateVersion } =
    useValidateArnoTemplateVersion();
  const { publishTemplateVersion, isPublishingTemplateVersion } =
    usePublishArnoTemplateVersion();

  const [sections, setSections] = useState<IEditorSection[]>([]);
  const [loaded, setLoaded] = useState<IEditorSection[]>([]);
  const [current, setCurrent] = useState(0);
  // Der Vorgang fuer die Vorschauwerte. Er gehoert zur Arbeitsweise der Bearbeiterin, nicht zum
  // Baum — deshalb im Browser gemerkt und nicht gespeichert.
  const [previewTransaction, setPreviewTransaction] = useState<number | null>(
    () => {
      try {
        const gemerkt = window.localStorage.getItem('arnoPreviewTransaction');
        return gemerkt ? Number(gemerkt) : null;
      } catch {
        return null;
      }
    }
  );
  const [validation, setValidation] = useState<IArnoValidation | null>(null);
  // Nur ein kopierter Baum hat offene Stellen; fuer einen selbst geschriebenen ist die
  // Abfrage leer und die Uebersicht erscheint gar nicht.
  const { migration, refetchMigration } = useArnoTemplateMigration(versionId);
  const [addAnchor, setAddAnchor] = useState<null | HTMLElement>(null);
  // Standardansicht: der Abschnitt, wie er sich liest. »Struktur« ist die bisherige Liste zum
  // Umordnen; die Wahl gehört zur Arbeitsweise und bleibt im Browser (#1245, Teil 43).
  const [view, setView] = useState<'lesen' | 'struktur'>(() => {
    try {
      return window.localStorage.getItem('arnoEditorView') === 'struktur'
        ? 'struktur'
        : 'lesen';
    } catch {
      return 'lesen';
    }
  });
  const [selection, setSelection] = useState<ReaderSelection | null>(null);
  // Abschnitte (Liste) oder Baukasten (Fläche) — beide auf denselben `sections`. Die Wahl
  // gehört zur Arbeitsweise und bleibt im Browser.
  const [mode, setMode] = useState<'abschnitte' | 'baukasten'>(() => {
    try {
      return window.localStorage.getItem('arnoEditorMode') === 'baukasten'
        ? 'baukasten'
        : 'abschnitte';
    } catch {
      return 'abschnitte';
    }
  });
  const changeMode = (next: 'abschnitte' | 'baukasten') => {
    setMode(next);
    setSelection(null);
    try {
      window.localStorage.setItem('arnoEditorMode', next);
    } catch {
      /* nur Bequemlichkeit */
    }
  };
  const changeView = (next: 'lesen' | 'struktur') => {
    setView(next);
    setSelection(null);
    try {
      window.localStorage.setItem('arnoEditorView', next);
    } catch {
      /* nur Bequemlichkeit */
    }
  };
  const [testOpen, setTestOpen] = useState(false);

  useEffect(() => {
    if (templateContent) {
      setSections(templateContent.sections);
      setLoaded(templateContent.sections);
      setValidation(null);
    }
  }, [templateContent]);

  /** Einen Abschnitt anlegen und gleich oeffnen — aus der Liste wie aus dem Leerzustand. */
  const addSection = () => {
    setSections(list => [...list, newSection(list)]);
    setCurrent(sections.length);
  };

  const editable = Boolean(templateContent?.editable);
  const dirty = isDirty(loaded, sections);

  // Ungespeicherte Arbeit nicht still verlieren (#1245, Teil 43): Schließen oder Neuladen des Tabs
  // fragt der Browser nach, der Zurück-Pfeil fragt selbst. Vorher ging beides kommentarlos.
  useEffect(() => {
    if (!dirty) {
      return undefined;
    }
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const leave = async () => {
    if (dirty) {
      const confirmed = await showConfirmation({
        title: 'Ungespeicherte Änderungen verwerfen?',
        message:
          'In diesem Entscheidungsbaum gibt es Änderungen, die noch nicht gespeichert sind. Beim Verlassen gehen sie verloren.',
        confirmButtonText: 'Verwerfen und verlassen',
        abortButtonText: 'Weiter bearbeiten',
      });
      if (!confirmed) {
        return;
      }
    }
    navigate('/admin/arnotop/entscheidungsbaeume');
  };
  const questions = useMemo(
    () => questionChoices(sections, templateContent?.questions ?? []),
    [sections, templateContent]
  );
  // Der Feldkatalog benennt die Pfade, die in den Zusammenfassungen vorkommen. Ohne ihn stuende
  // in der Elementliste der technische Pfad — das ist keine Sprache fuer eine Urkunde.
  const { selectorCatalog } = useArnoSelectorCatalog(previewTransaction);
  const fieldLabel = useMemo(
    () => fieldLabelResolver(selectorCatalog),
    [selectorCatalog]
  );
  const readerContext = useMemo(
    () => ({ questions, fieldLabel, sample: sampleResolver(selectorCatalog) }),
    [questions, fieldLabel, selectorCatalog]
  );
  const section = sections[current];
  // Text wird an Ort und Stelle bearbeitet (#1245, Teil 48); alles andere im Detailbereich.
  const inlinePath =
    editable &&
    section &&
    selection?.kind === 'node' &&
    editableInPlace(nodeAt(section.nodes, selection.path))
      ? selection.path
      : null;
  const liveReaderContext = {
    ...readerContext,
    editingPath: inlinePath,
    renderEditor: (node: IEditorSection['nodes'][number], path: number[]) => {
      const top = path.length === 1 ? path[0] : null;
      const count = section?.nodes.length ?? 0;
      return (
        <InlineTextEdit
          node={node}
          initialCursor={
            selection?.kind === 'node'
              ? { paragraph: selection.paragraph, offset: selection.offset }
              : undefined
          }
          questions={questions}
          idLegalTransaction={previewTransaction}
          onChange={paragraphs =>
            patchNodes(nodes => {
              const at = nodeAt(nodes, path);
              return at
                ? replaceNodeAt(nodes, path, {
                    ...at,
                    paragraphs,
                    text: undefined,
                  })
                : nodes;
            })
          }
          onDone={() => setSelection(null)}
          onMove={
            top === null
              ? undefined
              : delta => {
                  patchNodes(nodes => moveItem(nodes, top, delta));
                  setSelection({ kind: 'node', path: [top + delta] });
                }
          }
          canMoveUp={top !== null && top > 0}
          canMoveDown={top !== null && top < count - 1}
          onDuplicate={
            top === null
              ? undefined
              : () =>
                  patchNodes(nodes =>
                    insertAt(
                      nodes,
                      top + 1,
                      duplicateNode(
                        nodes[top],
                        questions.map(item => item.questionId)
                      )
                    )
                  )
          }
          onRemove={
            top === null
              ? undefined
              : () => {
                  patchNodes(nodes => removeAt(nodes, top));
                  setSelection(null);
                }
          }
        />
      );
    },
  };
  const blockTargets = useMemo(
    () =>
      blockTargetsOf(
        sections,
        section?.sectionId,
        templateContent?.subBlocks ?? []
      ),
    [sections, section, templateContent]
  );

  const patchSection = (values: Partial<IEditorSection>) =>
    setSections(list =>
      replaceAt(list, current, { ...list[current], ...values })
    );
  /** Knoten des Abschnitts ändern — immer am aktuellen Stand, nie an einem gemerkten. */
  const patchNodes = (
    update: (_nodes: IEditorSection['nodes']) => IEditorSection['nodes']
  ) =>
    setSections(list =>
      replaceAt(list, current, {
        ...list[current],
        nodes: update(list[current].nodes),
      })
    );

  const changePreviewTransaction = (next: number | null) => {
    setPreviewTransaction(next);
    try {
      if (next) {
        window.localStorage.setItem('arnoPreviewTransaction', String(next));
      } else {
        window.localStorage.removeItem('arnoPreviewTransaction');
      }
    } catch {
      // Ein Browser ohne Speicher ist kein Grund, die Vorschau zu verweigern.
    }
  };

  /** Ein Element ans Ende des Abschnitts setzen.
   *
   * Die schon vergebenen Fragekennungen kommen aus dem GANZEN Baum, nicht nur aus diesem
   * Abschnitt: sonst hiessen die ersten Fragen zweier Abschnitte gleich. */
  const appendNode = (kind: NodeKind) => {
    if (!section) {
      return;
    }
    patchSection({
      nodes: [
        ...section.nodes,
        newNode(
          kind,
          questions.map(item => item.questionId)
        ),
      ],
    });
    // In der Leseansicht ist ein neues, leeres Element kaum zu treffen — es öffnet sich gleich
    // zum Bearbeiten.
    if (view === 'lesen') {
      setSelection({ kind: 'node', path: [section.nodes.length] });
    }
  };

  const save = async () => {
    if (!templateContent) {
      return;
    }
    try {
      const saved = await saveTemplateContent({
        idDecisionTemplateVersion: versionId,
        sections,
        contentHash: templateContent.contentHash,
      });
      setSections(saved.sections);
      setLoaded(saved.sections);
      showAlert({ severity: 'success', text: 'Gespeichert.' });
      await refetchTemplateContent();
    } catch (error) {
      showAlert({
        severity: 'error',
        text: `Speichern fehlgeschlagen: ${apiErrorText(error)}`,
      });
    }
  };

  /** Von einer Meldung zu ihrem Abschnitt springen (§32). */
  const jumpToIssue = (issue: IArnoValidationIssue) => {
    if (!issue.sectionId) {
      return;
    }
    const index = sections.findIndex(
      section => section.sectionId === issue.sectionId
    );
    if (index >= 0) {
      setCurrent(index);
    }
  };

  const check = async () => {
    try {
      setValidation(await validateTemplateVersion(versionId));
    } catch (error) {
      showAlert({ severity: 'error', text: 'Die Prüfung ist fehlgeschlagen.' });
    }
  };

  const publish = async () => {
    const result = await validateTemplateVersion(versionId);
    setValidation(result);
    if (!result.publishable) {
      showAlert({
        severity: 'error',
        text: `Noch ${result.errors} Fehler — der Baum lässt sich nicht veröffentlichen.`,
      });
      return;
    }
    const confirmed = await showConfirmation({
      title: 'Version veröffentlichen?',
      message:
        'Ab dann wird diese Version für die Urkundenerzeugung verwendet. Sie lässt sich danach nicht mehr ändern; für Änderungen entsteht eine neue Version.',
      confirmButtonText: 'Veröffentlichen',
      abortButtonText: 'Abbrechen',
    });
    if (!confirmed) {
      return;
    }
    try {
      await publishTemplateVersion(versionId);
      showAlert({
        severity: 'success',
        text: 'Die Version ist veröffentlicht.',
      });
      await refetchTemplateContent();
    } catch (error) {
      showAlert({
        severity: 'error',
        text:
          error instanceof Error
            ? `Veröffentlichen fehlgeschlagen: ${apiErrorText(error)}`
            : 'Veröffentlichen fehlgeschlagen.',
      });
    }
  };

  const removeSection = async (index: number) => {
    const confirmed = await showConfirmation({
      title: 'Abschnitt löschen?',
      message: `„${sections[index].title}" wird mit allen Elementen entfernt. Beim Speichern ist das endgültig.`,
      confirmButtonText: 'Löschen',
      abortButtonText: 'Abbrechen',
    });
    if (confirmed) {
      setSections(list => removeAt(list, index));
      setCurrent(previous =>
        Math.max(0, Math.min(previous, sections.length - 2))
      );
    }
  };

  if (isLoadingTemplateContent) {
    return (
      <Stack alignItems='center' sx={{ py: 8 }}>
        <CircularProgress />
      </Stack>
    );
  }

  if (!templateContent) {
    return (
      <Alert severity='warning' sx={{ m: 2 }}>
        Diese Version gibt es nicht oder sie gehört zu einem importierten Baum.
      </Alert>
    );
  }

  const lifecycle = lifecycleChip(templateContent.lifecycleStatus);

  return (
    <Box sx={{ p: 1 }}>
      {/*
        Titel und Werkzeuge in zwei Zeilen. Vorher stand alles in einem umbrechenden Stack mit
        `spacing`; dessen Ränder vertragen sich nicht mit dem Umbruch, und bei 1 440 px lief
        »Mit Vorgang testen« rechts aus dem Bild, »Prüfen« war gar nicht mehr zu sehen
        (Sichtprüfung Teil 40).
      */}
      <Stack direction='row' alignItems='flex-start' sx={{ mb: 1, gap: 1 }}>
        <Tooltip title='Zurück zur Übersicht'>
          <IconButton
            onClick={() => void leave()}
            aria-label='Zurück zur Übersicht'
          >
            <ArrowBack />
          </IconButton>
        </Tooltip>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Stack
            direction='row'
            alignItems='center'
            sx={{ gap: 1, flexWrap: 'wrap' }}
          >
            <Typography variant='h6'>{templateContent.name}</Typography>
            <Chip
              size='small'
              color={lifecycle.color}
              label={lifecycle.label}
            />
            <Chip
              size='small'
              variant='outlined'
              label={
                templateContent.isPublished
                  ? `Version ${templateContent.versionNumber} · veröffentlicht`
                  : `Version ${templateContent.versionNumber} · Entwurf`
              }
            />
            {templateVersions.length > 1 && (
              <Chip
                size='small'
                variant='outlined'
                label={`${templateVersions.length} Versionen`}
              />
            )}
          </Stack>
          {templateContent.description && (
            <Typography variant='caption' color='text.secondary'>
              {templateContent.description}
            </Typography>
          )}
        </Box>
      </Stack>
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='flex-end'
        sx={{ mb: 2, gap: 1, flexWrap: 'wrap' }}
      >
        <Stack
          direction='row'
          alignItems='center'
          sx={{ mr: 'auto', gap: 1.5, flexWrap: 'wrap' }}
        >
          <ToggleButtonGroup
            size='small'
            exclusive
            value={mode}
            onChange={(_, next) => next && changeMode(next)}
            aria-label='Arbeitsweise'
          >
            <ToggleButton value='abschnitte'>Abschnitte</ToggleButton>
            <ToggleButton value='baukasten'>Baukasten</ToggleButton>
          </ToggleButtonGroup>
          <PreviewTransactionPicker
            value={previewTransaction}
            onChange={changePreviewTransaction}
          />
        </Stack>
        <Button startIcon={<PlayArrow />} onClick={() => setTestOpen(true)}>
          Mit Vorgang testen
        </Button>
        <Button onClick={check} disabled={isValidatingTemplateVersion}>
          Prüfen
        </Button>
        <Button
          startIcon={<Save />}
          onClick={save}
          disabled={!editable || !dirty || isSavingTemplateContent}
        >
          Speichern
        </Button>
        <Button
          variant='contained'
          startIcon={<Publish />}
          onClick={publish}
          disabled={!editable || dirty || isPublishingTemplateVersion}
        >
          Veröffentlichen
        </Button>
      </Stack>

      {!editable && (
        <Alert severity='info' sx={{ mb: 2 }}>
          Diese Version ist veröffentlicht und wird nicht mehr verändert. Für
          Änderungen in der Übersicht eine neue Version anlegen.
        </Alert>
      )}
      {dirty && editable && (
        <Alert severity='warning' sx={{ mb: 2 }}>
          Ungespeicherte Änderungen. Veröffentlichen geht erst nach dem
          Speichern.
        </Alert>
      )}
      <MigrationPanel
        migration={migration}
        idDecisionTemplateVersion={versionId}
        editable={editable}
        onJumpToSection={sectionId => {
          const index = sections.findIndex(
            item => item.sectionId === sectionId
          );
          if (index >= 0) setCurrent(index);
        }}
        onResolved={() => {
          void refetchMigration();
          void refetchTemplateContent();
          void check();
        }}
      />

      {/*
        Fehler zuerst und unübersehbar: der falsch geschriebene Selektor
        »Lastname« stand schon einmal als kleiner Hinweis da und wurde
        übergangen — bis er in Word einen ganzen Satz verschwinden ließ (#1245).
      */}
      {validation && validation.errors > 0 && (
        <Alert severity='error' sx={{ mb: 2 }}>
          <AlertTitle>
            Veröffentlichung nicht möglich — {validation.errors}{' '}
            {validation.errors === 1 ? 'Fehler' : 'Fehler'}
          </AlertTitle>
          <Box component='ul' sx={{ m: 0, pl: 2 }}>
            {validation.issues
              .filter(issue => issue.severity === 'ERROR')
              .slice(0, 20)
              .map((issue, index) => (
                <li key={`error-${issue.code}-${index}`}>
                  <Link
                    component='button'
                    type='button'
                    variant='body2'
                    underline='hover'
                    align='left'
                    onClick={() => jumpToIssue(issue)}
                    sx={{ textAlign: 'left' }}
                  >
                    {issue.message}
                  </Link>
                </li>
              ))}
          </Box>
        </Alert>
      )}
      {validation && (
        <Alert
          severity={
            validation.publishable
              ? validation.warnings
                ? 'warning'
                : 'success'
              : 'info'
          }
          sx={{ mb: 2 }}
        >
          <AlertTitle>
            {validation.publishable
              ? `Veröffentlichung möglich${validation.warnings ? ` — ${validation.warnings} Hinweis(e)` : ''}`
              : `${validation.warnings} Hinweis(e)`}
          </AlertTitle>
          <Box component='ul' sx={{ m: 0, pl: 2 }}>
            {validation.issues
              .filter(issue => issue.severity === 'WARNING')
              .slice(0, 12)
              .map((issue, index) => (
                <li key={`warning-${issue.code}-${index}`}>
                  <Link
                    component='button'
                    type='button'
                    variant='body2'
                    underline='hover'
                    align='left'
                    onClick={() => jumpToIssue(issue)}
                    sx={{ textAlign: 'left' }}
                  >
                    {issue.message}
                  </Link>
                </li>
              ))}
          </Box>
        </Alert>
      )}

      {mode === 'baukasten' ? (
        <DecisionTreeCanvas
          sections={sections}
          onChange={setSections}
          editable={editable}
          questions={questions}
          externalQuestions={templateContent.questions ?? []}
          subBlocks={templateContent.subBlocks ?? []}
          idLegalTransaction={previewTransaction}
          issues={validation?.issues ?? []}
          focusSectionIndex={current}
          onFocusSection={setCurrent}
          resetKey={templateContent.contentHash}
          fieldLabel={fieldLabel}
          onOpenInList={index => {
            setCurrent(index);
            changeMode('abschnitte');
          }}
        />
      ) : (
        <Stack direction='row' spacing={2} alignItems='flex-start'>
          <Box
            sx={{
              width: 280,
              flexShrink: 0,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <Stack direction='row' alignItems='center' sx={{ px: 1.5, py: 1 }}>
              <Typography variant='subtitle2' sx={{ flexGrow: 1 }}>
                Abschnitte
              </Typography>
              <Tooltip title='Abschnitt hinzufügen'>
                <span>
                  <IconButton
                    size='small'
                    aria-label='Abschnitt hinzufügen'
                    disabled={!editable}
                    onClick={addSection}
                  >
                    <Add fontSize='small' />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
            <Divider />
            <List dense disablePadding>
              {sections.map((item, index) => (
                <ListItemButton
                  key={item.sectionId}
                  selected={index === current}
                  onClick={() => setCurrent(index)}
                >
                  <ListItemText
                    primary={`${index + 1}. ${item.title}`}
                    secondary={sectionSummary(item)}
                  />
                  <Stack direction='row'>
                    <IconButton
                      size='small'
                      aria-label={`Abschnitt ${index + 1} nach oben`}
                      disabled={!editable || index === 0}
                      onClick={event => {
                        event.stopPropagation();
                        setSections(list => moveItem(list, index, -1));
                        setCurrent(index - 1);
                      }}
                    >
                      <ArrowUpward fontSize='inherit' />
                    </IconButton>
                    <IconButton
                      size='small'
                      aria-label={`Abschnitt ${index + 1} nach unten`}
                      disabled={!editable || index === sections.length - 1}
                      onClick={event => {
                        event.stopPropagation();
                        setSections(list => moveItem(list, index, 1));
                        setCurrent(index + 1);
                      }}
                    >
                      <ArrowDownward fontSize='inherit' />
                    </IconButton>
                  </Stack>
                </ListItemButton>
              ))}
              {sections.length === 0 && (
                <Box sx={{ p: 2 }}>
                  <Typography variant='body2' color='text.secondary'>
                    Noch kein Abschnitt. Ein Baum braucht mindestens einen.
                  </Typography>
                  <Button
                    size='small'
                    variant='outlined'
                    startIcon={<Add />}
                    disabled={!editable}
                    onClick={addSection}
                    sx={{ mt: 1.5 }}
                  >
                    Ersten Abschnitt anlegen
                  </Button>
                </Box>
              )}
            </List>
          </Box>

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            {!section ? (
              <Box
                sx={{
                  border: 1,
                  borderStyle: 'dashed',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 6,
                  textAlign: 'center',
                }}
              >
                <Typography variant='subtitle1' gutterBottom>
                  Ein Abschnitt ist ein Teil der Urkunde
                </Typography>
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ maxWidth: 440, mx: 'auto' }}
                >
                  Zum Beispiel »§ 1 Beteiligte« oder »Kaufpreis«. In einen
                  Abschnitt kommen Text, Datenfelder, Fragen, Bedingungen und
                  Tabellen.
                </Typography>
                <Button
                  variant='contained'
                  startIcon={<Add />}
                  disabled={!editable}
                  onClick={addSection}
                  sx={{ mt: 3 }}
                >
                  Ersten Abschnitt anlegen
                </Button>
              </Box>
            ) : (
              <Stack spacing={2}>
                <Stack
                  direction='row'
                  spacing={1.5}
                  alignItems='flex-start'
                  sx={{ flexWrap: 'wrap', gap: 1.5 }}
                >
                  <TextField
                    size='small'
                    label='Abschnitt'
                    value={section.title}
                    onChange={event =>
                      patchSection({ title: event.target.value })
                    }
                    disabled={!editable}
                    sx={{ minWidth: 280, flexGrow: 1 }}
                  />
                  <TextField
                    size='small'
                    select
                    label='Standardmäßig'
                    value={section.enabled ? 'on' : 'off'}
                    onChange={event =>
                      patchSection({ enabled: event.target.value === 'on' })
                    }
                    disabled={!editable}
                    sx={{ minWidth: 160 }}
                  >
                    <MenuItem value='on'>aktiv</MenuItem>
                    <MenuItem value='off'>nicht aktiv</MenuItem>
                  </TextField>
                  <Tooltip title='Abschnitt löschen'>
                    <span>
                      <IconButton
                        color='error'
                        disabled={!editable}
                        onClick={() => removeSection(current)}
                      >
                        <DeleteOutline />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>

                <ConditionBuilder
                  condition={section.condition}
                  onChange={condition => patchSection({ condition })}
                  questions={questions}
                  disabled={!editable}
                  idLegalTransaction={previewTransaction}
                />

                <ToggleButtonGroup
                  size='small'
                  exclusive
                  value={view}
                  onChange={(_, next) => next && changeView(next)}
                  aria-label='Ansicht'
                >
                  <ToggleButton value='lesen'>Lesen</ToggleButton>
                  <ToggleButton value='struktur'>Struktur</ToggleButton>
                </ToggleButtonGroup>

                {view === 'lesen' && section.nodes.length > 0 && (
                  <Box
                    sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      px: { xs: 2, md: 4 },
                      py: 3,
                      bgcolor: 'background.paper',
                    }}
                  >
                    <SectionReader
                      nodes={section.nodes}
                      context={liveReaderContext}
                      selection={selection}
                      onSelect={setSelection}
                    />
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{ display: 'block', mt: 2 }}
                    >
                      {previewTransaction
                        ? 'Datenfelder zeigen die Werte des Vorschauvorgangs. Zum Bearbeiten auf eine Stelle klicken.'
                        : 'Zum Bearbeiten auf eine Stelle klicken. Mit einem Vorschauvorgang zeigen Datenfelder ihre Werte.'}
                    </Typography>
                  </Box>
                )}

                {view === 'struktur' &&
                  section.nodes.map((node, index) => (
                    <Accordion
                      key={`${section.sectionId}-${index}`}
                      disableGutters
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMore />}
                        sx={{
                          '& .MuiAccordionSummary-content': { minWidth: 0 },
                        }}
                      >
                        <Stack
                          direction='row'
                          spacing={1}
                          alignItems='center'
                          sx={{ width: '100%', minWidth: 0 }}
                        >
                          <Chip
                            size='small'
                            variant='outlined'
                            label={NODE_KIND_LABEL[node.type]}
                          />
                          {/* `minWidth: 0`, sonst schiebt ein langer Fragetext die Zeile über die Karte hinaus. */}
                          <Typography
                            variant='body2'
                            sx={{ flexGrow: 1, minWidth: 0 }}
                            noWrap
                          >
                            {nodeSummary(node)}
                          </Typography>
                          {node.condition && (
                            <Typography
                              variant='caption'
                              color='text.secondary'
                              noWrap
                              sx={{ maxWidth: 260 }}
                            >
                              {conditionSummary(
                                node.condition,
                                questions,
                                fieldLabel
                              )}
                            </Typography>
                          )}
                          <IconButton
                            size='small'
                            disabled={!editable || index === 0}
                            onClick={event => {
                              event.stopPropagation();
                              patchSection({
                                nodes: moveItem(section.nodes, index, -1),
                              });
                            }}
                          >
                            <ArrowUpward fontSize='inherit' />
                          </IconButton>
                          <IconButton
                            size='small'
                            disabled={
                              !editable || index === section.nodes.length - 1
                            }
                            onClick={event => {
                              event.stopPropagation();
                              patchSection({
                                nodes: moveItem(section.nodes, index, 1),
                              });
                            }}
                          >
                            <ArrowDownward fontSize='inherit' />
                          </IconButton>
                          <IconButton
                            size='small'
                            disabled={!editable}
                            onClick={event => {
                              event.stopPropagation();
                              patchSection({
                                nodes: insertAt(
                                  section.nodes,
                                  index + 1,
                                  duplicateNode(
                                    node,
                                    questions.map(item => item.questionId)
                                  )
                                ),
                              });
                            }}
                          >
                            <ContentCopy fontSize='inherit' />
                          </IconButton>
                          <IconButton
                            size='small'
                            color='error'
                            disabled={!editable}
                            onClick={event => {
                              event.stopPropagation();
                              patchSection({
                                nodes: removeAt(section.nodes, index),
                              });
                            }}
                          >
                            <DeleteOutline fontSize='inherit' />
                          </IconButton>
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails>
                        <NodeEditor
                          node={node}
                          onChange={next =>
                            patchSection({
                              nodes: replaceAt(section.nodes, index, next),
                            })
                          }
                          questions={questions}
                          blockTargets={blockTargets}
                          idLegalTransaction={previewTransaction}
                          disabled={!editable}
                        />
                      </AccordionDetails>
                    </Accordion>
                  ))}

                {section.nodes.length === 0 && (
                  <Box
                    sx={{
                      border: 1,
                      borderStyle: 'dashed',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 3,
                    }}
                  >
                    <Typography variant='body2' color='text.secondary'>
                      Dieser Abschnitt ist noch leer. Fangen Sie mit Text an und
                      setzen Sie Datenfelder, Fragen oder Bedingungen hinein.
                    </Typography>
                  </Box>
                )}

                {/* Eine kleine Leiste: die fünf täglichen Bausteine direkt, der Rest
                  hinter »Weitere«. Vorher standen elf Knotenarten gleichrangig
                  nebeneinander, darunter »DiNo-Variable« und »Ende«. */}
                <Stack
                  direction='row'
                  spacing={1}
                  sx={{ flexWrap: 'wrap', rowGap: 1, alignItems: 'center' }}
                >
                  {COMMON_NODE_KINDS.map(kind => (
                    <Tooltip key={kind} title={NODE_KIND_HINT[kind] ?? ''}>
                      <span>
                        <Button
                          size='small'
                          variant='outlined'
                          startIcon={<Add />}
                          disabled={!editable}
                          onClick={() => appendNode(kind)}
                        >
                          {NODE_KIND_LABEL[kind]}
                        </Button>
                      </span>
                    </Tooltip>
                  ))}
                  <Button
                    size='small'
                    disabled={!editable}
                    onClick={event => setAddAnchor(event.currentTarget)}
                  >
                    Weitere …
                  </Button>
                  <Menu
                    anchorEl={addAnchor}
                    open={Boolean(addAnchor)}
                    onClose={() => setAddAnchor(null)}
                  >
                    {MORE_NODE_KINDS.map(kind => (
                      <MenuItem
                        key={kind}
                        onClick={() => {
                          appendNode(kind);
                          setAddAnchor(null);
                        }}
                        sx={{ display: 'block', py: 1 }}
                      >
                        <Typography variant='body2'>
                          {NODE_KIND_LABEL[kind]}
                        </Typography>
                        {NODE_KIND_HINT[kind] && (
                          <Typography variant='caption' color='text.secondary'>
                            {NODE_KIND_HINT[kind]}
                          </Typography>
                        )}
                      </MenuItem>
                    ))}
                  </Menu>
                </Stack>
              </Stack>
            )}
          </Box>
        </Stack>
      )}

      {section && mode === 'abschnitte' && (
        <ReaderDetails
          selection={inlinePath ? null : selection}
          nodes={section.nodes}
          context={readerContext}
          editable={editable}
          editorFor={index =>
            section.nodes[index] ? (
              <NodeEditor
                node={section.nodes[index]}
                onChange={next =>
                  patchSection({
                    nodes: replaceAt(section.nodes, index, next),
                  })
                }
                questions={questions}
                blockTargets={blockTargets}
                idLegalTransaction={previewTransaction}
                disabled={!editable}
              />
            ) : null
          }
          onMove={(index, delta) => {
            patchSection({ nodes: moveItem(section.nodes, index, delta) });
            setSelection({ kind: 'node', path: [index + delta] });
          }}
          onDuplicate={index =>
            patchSection({
              nodes: insertAt(
                section.nodes,
                index + 1,
                duplicateNode(
                  section.nodes[index],
                  questions.map(item => item.questionId)
                )
              ),
            })
          }
          onRemove={index => {
            patchSection({ nodes: removeAt(section.nodes, index) });
            setSelection(null);
          }}
          onClose={() => setSelection(null)}
        />
      )}

      <TestRunDialog
        open={testOpen}
        onClose={() => setTestOpen(false)}
        idDecisionTemplateVersion={versionId}
        name={templateContent.name}
        dirty={dirty}
      />
    </Box>
  );
};
